<template>
  <div>
    <!-- 2026-10-18：维语使用RTL布局及独立组件语言，保留金额/号码原值。 -->
    <a-config-provider :locale="locale" :direction="uiDirection">
      <div v-if="!maintenanceReady || maintenanceConfig.enabled" class="ui-maintenance-language"><LanguageSelect /></div>
      <!-- 2026-09-03：维护入口始终可达；其他管理页面按全局维护状态统一切换。 -->
      <router-view v-if="isMaintenanceAccessRoute" />
      <MaintenanceScreen
        v-else-if="maintenanceReady && maintenanceBlocked && !testerLogin"
        :config="maintenanceConfig"
        :checking="maintenanceChecking"
        @refresh="refreshMaintenance"
        @tester-login="beginTesterLogin"
      />
      <!-- 2026-09-08：测试登录仅放开认证入口，服务端短期资格通过后才展示原有管理页面。 -->
      <MaintenanceTesterLogin v-else-if="maintenanceReady && maintenanceBlocked && testerLogin"
        :checking="testerChecking" @authenticated="verifyTesterLogin" @cancel="exitMaintenanceTest" />
      <router-view v-else-if="maintenanceReady" />
      <div v-else class="maintenance-checking">正在检查系统状态...</div>
      <div v-if="testerAllowed" class="maintenance-test-banner">维护测试中 <a-button size="small" @click="exitMaintenanceTest">退出维护测试</a-button></div>
    </a-config-provider>
  </div>  
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { ADMIN_SKIN_SAVED, createAdminSkinSync } from '@/utils/adminSkin'
import { LEFT_SIDE_MAX_WIDTH,LEFT_SIDE_MIN_WIDTH } from '@/constants'
import store from '@/store'
import MaintenanceScreen from '@/components/maintenance/MaintenanceScreen.vue'
import MaintenanceTesterLogin from '@/components/maintenance/MaintenanceTesterLogin.vue'
import { createOperatorMaintenanceTester } from '@/utils/maintenanceTester'
import request from '@/utils/request'
// 2026-09-29：管理端全局检查新通知，提示音跟随当前登录账号与维护状态启停。
import { notificationSound } from '@/utils/notificationSoundRuntime'
import {createSessionSocket} from '@/utils/sessionSocket'
import router from '@/router'
import { message } from 'ant-design-vue'
import {
  clearMaintenanceBypass,
  DEFAULT_MAINTENANCE_CONFIG,
  hasMaintenanceBypass,
  loadPlatformMaintenance,
  MAINTENANCE_ACCESS_PATH,
  MAINTENANCE_BYPASS_CHANGED_EVENT,
  MAINTENANCE_CHANGED_EVENT,
  normalizeMaintenanceConfig,
  SystemMaintenanceConfig
} from '@/utils/maintenance'
// import locale from '@/i18n/lang/zh'
import zhCN from 'ant-design-vue/lib/locale-provider/zh_CN';
import enGB from 'ant-design-vue/lib/locale-provider/en_GB';
import { displayLanguage } from '@/i18n/display'
import ugLocale from '@/i18n/component-locale'
import LanguageSelect from '@/i18n/LanguageSelect.vue'
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

export interface Operator {
  Name: string
  Age: number
}
export default defineComponent({
  components: { LanguageSelect, MaintenanceScreen, MaintenanceTesterLogin },
  setup() {
    const store = useStore()
    const route = useRoute()
    const skinSync = createAdminSkinSync()
    const skinOrgId = computed(() => {
      const own = store.getters.token && Number(store.getters.userInfo?.orgId)
      const entry = Number(route.query.store || route.query.orgId)
      return own || (Number.isSafeInteger(entry) && entry > 0 ? entry : 1000)
    })
    const refreshSkin = () => { void skinSync.refresh(skinOrgId.value) }
    watch(skinOrgId, refreshSkin, { immediate: true })
    const person: Operator = {
      Name: 'jax ',
      Age: 34
    }
    const sockets=createSessionSocket({
      identity:()=>store.getters.token && store.getters.userInfo?.id && maintenanceReady.value && !maintenanceBlocked.value && !isMaintenanceAccessRoute.value
        ? JSON.stringify([store.getters.token,store.getters.userInfo.orgId,store.getters.userInfo.id]):'',
      url:()=>process.env.VUE_APP_BASE_WS+'org'+store.getters.userInfo.id,
      connect:url=>new WebSocket(url),changed:socket=>{store.state.app.socket=socket},
      message:event=>{store.getters.onmessage?.(event);store.getters.onsysmessage?.(event)}
    })
    const lang=computed(() => {
      if(store.getters.language=='zh'){
        dayjs.locale('zh-cn');
        return zhCN
      }else{
        dayjs.locale(store.getters.language === 'ug' ? 'ug-cn' : 'en');
        return store.getters.language === 'ug' ? ugLocale : enGB
      }

    })
    const maintenanceConfig = ref<SystemMaintenanceConfig>({ ...DEFAULT_MAINTENANCE_CONFIG })
    const maintenanceReady = ref(false)
    const maintenanceChecking = ref(false)
    const bypassRevision = ref(0)
    let maintenanceTimer = 0
    let appInitialized = false

    const isMaintenanceAccessRoute = computed(() => route.path === MAINTENANCE_ACCESS_PATH)
    const hasAuthorizedBypass = computed(() => {
      bypassRevision.value
      const user = store.getters.userInfo || {}
      return hasMaintenanceBypass(maintenanceConfig.value)
        && Boolean(store.getters.token)
        && Number(user.orgId) === 1000
        && Number(user.roleId) === 1
    })
    // 2026-09-08：测试放行绑定当前会话并定期重查名单，超管应急规则保持独立。
    const testerLogin = ref(false), testerChecking = ref(false), testerTick = ref(0)
    let testerExpiryTimer = 0, testerLoginTimer = 0
    const tester = createOperatorMaintenanceTester(() => ({
      token: store.getters.token || '', operatorId: Number(store.getters.userInfo?.id || 0), orgId: Number(store.getters.userInfo?.orgId || 0)
    }), async () => {
      const result: any = await request({ url: 'system/maintenanceTesterAccess', method: 'GET' })
      return result.data
    })
    const testerAllowed = computed(() => {
      testerTick.value; store.getters.token; store.getters.userInfo
      return tester.active(maintenanceConfig.value)
    })
    const clearTester = () => {
      tester.clear(); testerLogin.value = false; testerTick.value++
      window.clearTimeout(testerExpiryTimer); window.clearTimeout(testerLoginTimer)
    }
    const exitMaintenanceTest = async () => {
      clearTester()
      if (store.getters.token) await store.dispatch('user/logout')
    }
    const scheduleTesterExpiry = () => {
      window.clearTimeout(testerExpiryTimer)
      if (!tester.active(maintenanceConfig.value)) return
      testerExpiryTimer = window.setTimeout(() => {
        testerTick.value++
        if (!tester.active(maintenanceConfig.value)) exitMaintenanceTest()
      }, Math.max(1, tester.expiresAt() - Date.now()))
    }
    const verifyTesterLogin = async () => {
      if (testerChecking.value) return
      testerChecking.value = true
      try {
        const allowed = await tester.verify(maintenanceConfig.value)
        testerTick.value++
        if (!allowed) {
          await exitMaintenanceTest()
          message.warning('该账号未获本轮管理端维护测试资格，或资格已到期。')
          return
        }
        testerLogin.value = false; window.clearTimeout(testerLoginTimer); scheduleTesterExpiry()
        await router.replace('/')
      } finally { testerChecking.value = false }
    }
    const beginTesterLogin = async () => {
      if (testerChecking.value) return
      tester.begin(); testerLogin.value = true; testerTick.value++
      window.clearTimeout(testerLoginTimer)
      testerLoginTimer = window.setTimeout(() => exitMaintenanceTest(), 10 * 60 * 1000)
      if (store.getters.token && store.getters.userInfo?.id) await verifyTesterLogin()
    }
    const maintenanceBlocked = computed(() =>
      maintenanceConfig.value.enabled && !hasAuthorizedBypass.value && !testerAllowed.value)

    const initializeApplication = () => {
      if (appInitialized || isMaintenanceAccessRoute.value || maintenanceBlocked.value) return
      appInitialized = true

      document.body.style.setProperty('--sideBarWidth', store.state.app.sideBarOpened ? 'var(--sidebar-expanded-width, '+LEFT_SIDE_MAX_WIDTH+'px)' : LEFT_SIDE_MIN_WIDTH+'px')
      sockets.refresh()
      //generateNewStyle(store.getters.theme)
    }

    const refreshMaintenance = async () => {
      if (maintenanceChecking.value) return
      maintenanceChecking.value = true
      try {
        const snapshot = await loadPlatformMaintenance()
        maintenanceConfig.value = snapshot.config
        if (!snapshot.config.enabled) clearMaintenanceBypass()
      } catch (_error) {
        // 首次读取失败时放行，避免网络抖动被误判为维护；后续失败保留最近一次有效状态。
        if (!maintenanceReady.value) maintenanceConfig.value = { ...DEFAULT_MAINTENANCE_CONFIG }
      } finally {
        maintenanceReady.value = true
        maintenanceChecking.value = false
      }
      if (!maintenanceConfig.value.enabled) clearTester()
      else if (tester.attempting() && !testerLogin.value && !testerChecking.value) {
        testerChecking.value = true
        try {
          const allowed = await tester.verify(maintenanceConfig.value)
          testerTick.value++
          if (!allowed) await exitMaintenanceTest()
          else scheduleTesterExpiry()
        } finally { testerChecking.value = false }
      }
      initializeApplication()
    }

    const handleMaintenanceChanged = (event: Event) => {
      const customEvent = event as CustomEvent
      clearTester()
      maintenanceConfig.value = normalizeMaintenanceConfig(customEvent.detail)
      maintenanceReady.value = true
      initializeApplication()
    }
    const handleBypassChanged = () => {
      bypassRevision.value += 1
      initializeApplication()
    }
    const handleVisibilityChanged = () => {
      if (document.visibilityState === 'visible') { refreshMaintenance(); refreshSkin() }
    }

    watch([maintenanceReady,maintenanceBlocked,isMaintenanceAccessRoute],()=>{sockets.refresh();initializeApplication()})
    watch(() => [store.getters.token, store.getters.userInfo?.id, store.getters.userInfo?.orgId], () => {
      sockets.refresh()
      if (!testerLogin.value && tester.attempting()) clearTester()
    })
    const stopSoundWatch = watch([() => store.getters.token, () => store.getters.userInfo?.id,
      () => store.getters.userInfo?.orgId, maintenanceReady, maintenanceBlocked, isMaintenanceAccessRoute], () => {
      const user = store.getters.userInfo || {}
      const enabled = store.getters.token && user.id && user.orgId && maintenanceReady.value
        && !maintenanceBlocked.value && !isMaintenanceAccessRoute.value
      notificationSound.start(enabled ? `${user.orgId}:${user.id}` : '')
    }, { immediate: true })
    const unlockNotificationSound = (event: Event) => {
      if (event.isTrusted) void notificationSound.unlock()
    }
    const syncNotificationSound = (event: StorageEvent) => notificationSound.storageChanged(event.key)
    onMounted(async () => {
      document.addEventListener('pointerdown', unlockNotificationSound, { passive: true })
      document.addEventListener('keydown', unlockNotificationSound)
      window.addEventListener('storage', syncNotificationSound)
      window.addEventListener(ADMIN_SKIN_SAVED, refreshSkin)
      window.addEventListener(MAINTENANCE_CHANGED_EVENT, handleMaintenanceChanged)
      window.addEventListener(MAINTENANCE_BYPASS_CHANGED_EVENT, handleBypassChanged)
      document.addEventListener('visibilitychange', handleVisibilityChanged)
      await refreshMaintenance()
      maintenanceTimer = window.setInterval(refreshMaintenance, 15000)
    })
    onBeforeUnmount(() => {
      sockets.dispose()
      skinSync.dispose(); window.removeEventListener(ADMIN_SKIN_SAVED, refreshSkin)
      stopSoundWatch(); notificationSound.dispose()
      document.removeEventListener('pointerdown', unlockNotificationSound)
      document.removeEventListener('keydown', unlockNotificationSound)
      window.removeEventListener('storage', syncNotificationSound)
      window.clearInterval(maintenanceTimer)
      clearTester()
      window.removeEventListener(MAINTENANCE_CHANGED_EVENT, handleMaintenanceChanged)
      window.removeEventListener(MAINTENANCE_BYPASS_CHANGED_EVENT, handleBypassChanged)
      document.removeEventListener('visibilitychange', handleVisibilityChanged)
    })
    return {
      person,
      locale:lang,
      uiDirection: computed(() => displayLanguage.value === 'ug' ? 'rtl' : 'ltr'),
      maintenanceConfig,
      maintenanceReady,
      maintenanceChecking,
      maintenanceBlocked,
      isMaintenanceAccessRoute,
      refreshMaintenance, testerLogin, testerChecking, testerAllowed, beginTesterLogin, verifyTesterLogin, exitMaintenanceTest
    }
  }
})
</script>
<style lang="scss">
#app {
  overflow: hidden;
}
.maintenance-test-banner { position: fixed; right: 16px; bottom: 20px; z-index: 10000; padding: 10px 16px; border: 1px solid #91caff; border-radius: 10px; background: #e6f4ff; color: #0958d9; }
.maintenance-checking {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  color: #667085;
  background: #f5f9ff;
}
</style>
